import React, { useEffect } from 'react';
import Map, { Marker } from 'react-map-gl';
import { Coordinates } from '@/types/common-types';
import { searchAddress, mapboxToken } from './mapbox';

interface SiteLocationMapProps {
  coordinates: Coordinates;
  onCoordinatesChange: (coordinates: Coordinates) => void;
  onAddressChange: (address: string, lat: number, lon: number) => void;
  initialViewState?: {
    longitude: number;
    latitude: number;
    zoom: number;
  };
  initialAddress?: string; // New prop to accept an initial address string
}

const SiteLocationMap: React.FC<SiteLocationMapProps> = React.memo(
  ({
    coordinates,
    onCoordinatesChange,
    onAddressChange,
    initialViewState = {
      longitude: -174.7645,
      latitude: -36.8509,
      zoom: 11,
    },
    initialAddress,
  }) => {
    const [viewState, setViewState] = React.useState(initialViewState);
    const [isInitialLoad, setIsInitialLoad] = React.useState(true);

    // Update viewState when coordinates change externally
    React.useEffect(() => {
      if (coordinates.lat !== viewState.latitude || coordinates.lng !== viewState.longitude) {
        setViewState((prev) => ({
          ...prev,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        }));
      }
    }, [coordinates]);

    // Geocode the initial address if provided
    useEffect(() => {
      const geocodeInitialAddress = async () => {
        if (initialAddress && isInitialLoad) {
          try {
            const results = await searchAddress(initialAddress);
            if (results && results.length > 0) {
              const result = results[0];
              const newCoordinates = {
                lat: result.center[1],
                lng: result.center[0],
              };

              // Update coordinates
              onCoordinatesChange(newCoordinates);

              // Update address if callback is provided
              if (result.address) {
                onAddressChange(result.address, result.center[1], result.center[0]);
              }

              // Update view state
              setViewState({
                latitude: result.center[1],
                longitude: result.center[0],
                zoom: 15, // Zoom in closer for a specific address
              });
            }
          } catch (error) {
            console.error('Error geocoding initial address:', error);
          } finally {
            setIsInitialLoad(false);
          }
        }
      };

      geocodeInitialAddress();
    }, [initialAddress, isInitialLoad, onCoordinatesChange, onAddressChange]);

    return (
      <div className="h-[300px] w-full overflow-hidden rounded-lg border">
        <Map
          initialViewState={initialViewState}
          mapboxAccessToken={mapboxToken}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          style={{ height: '100%', width: '100%' }}
          mapStyle={`mapbox://styles/watchful2024map/cm4by2a1h00pq01rc8q54abca${
            import.meta.env.DEV ? '/draft' : ''
          }`}
        >
          <Marker
            longitude={coordinates.lng}
            latitude={coordinates.lat}
            anchor="bottom"
            draggable
            onDragEnd={(e) => {
              const newCoordinates = {
                lat: e.lngLat.lat,
                lng: e.lngLat.lng,
              };
              onCoordinatesChange(newCoordinates);
              // Update address when marker is dragged
              searchAddress(`${e.lngLat.lat},${e.lngLat.lng}`)
                .then((results) => {
                  if (results.length > 0) {
                    onAddressChange(results[0].place_name, e.lngLat.lat, e.lngLat.lng);
                  }
                })
                .catch(console.error);
            }}
          >
            <img src="/icon/map-pin.svg" alt="location marker" />
          </Marker>
        </Map>
      </div>
    );
  }
);

export default SiteLocationMap;
